import api from 'plugin/api';
import { API_URL } from 'types/const';
import isJSON from 'utils/isJSON';
import jsonToFormData from 'utils/json/toFormData';
import format from 'utils/string/format';

/**
 * 신규 원료 코드 신청 목록 조회
 * @param {Object} params 요청 데이터
 * @param {string} [params.rwmtrCd] 원료 코드
 * @param {string} [params.rwmtrNm] 원료 명
 * @param {string} [params.mtrilGrpCd] 하위 분류
 * @return {Object} 응답 데이터
 */
async function requestList(params) {
  return api.get(API_URL.board.rawMaterial.default, { params });
}

/**
 * 신규 원료 코드 신청
 * @param {Object} data 요청 데이터
 * @param {string} data.rwmtrNm 원료 명
 * @param {string} data.usePurps 사용 목적
 * @param {string} data.mtrilGrpCd 하위 분류
 * @param {number[]} data.moisture Moist. 영양가
 * @param {number[]} data.cp CP 영양가
 * @param {number[]} data.cFat CFAT 영양가
 * @param {number[]} data.cFib CFIB 영양가
 * @param {number[]} data.ash ASH 영양가
 * @param {number[]} data.ca Ca 영양가
 * @param {number[]} data.p P 영양가
 * @param {number[]} data.starch Starch 영양가
 * @param {string[]} data.note 영양가 Note (Supplier, NIR, Wet)
 * @param {File} data.file 첨부 파일
 * @return {Object} 응답 데이터
 */
async function requestInsert(data) {
  if (isJSON(data)) {
    data = jsonToFormData(data);
  }

  return api.post(API_URL.board.rawMaterial.default, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

/**
 * 신규 원료 코드 신청 수정
 * @param {number} seq 신규 원료 코드 신청 번호
 * @param {Object} data 요청 데이터
 * @param {string} [data.rwmtrCd] 원료 코드
 * @param {string} data.rwmtrNm 원료 명
 * @param {string} data.usePurps 사용 목적
 * @param {string} data.mtrilGrpCd 하위 분류
 * @param {number[]} data.moisture Moist. 영양가
 * @param {number[]} data.cp CP 영양가
 * @param {number[]} data.cFat CFAT 영양가
 * @param {number[]} data.cFib CFIB 영양가
 * @param {number[]} data.ash ASH 영양가
 * @param {number[]} data.ca Ca 영양가
 * @param {number[]} data.p P 영양가
 * @param {number[]} data.starch Starch 영양가
 * @param {string[]} data.note 영양가 Note (Supplier, NIR, Wet)
 * @param {File} data.file 첨부 파일
 * @param {number[]} data.fileSeq 기존 첨부 파일 번호 목록
 * @return {Object} 응답 데이터
 */
async function requestUpdate(seq, data) {
  if (isJSON(data)) {
    data = jsonToFormData(data);
  }

  return api.put(format(API_URL.board.rawMaterial.inform, { seq }), data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

/**
 * 신규 원료 코드 신청 삭제
 * @param {number} seq 신규 원료 코드 신청 번호
 * @return {Object} 응답 데이터
 */
async function requestDelete(seq) {
  return api.delete(format(API_URL.board.rawMaterial.inform, { seq }));
}

/**
 * 신규 원료 코드 신청 정보 조회
 * @param {number} seq 신규 원료 코드 신청 번호
 * @return {Object} 응답 데이터
 */
async function requestInform(seq) {
  return api.get(format(API_URL.board.rawMaterial.inform, { seq }));
}

/**
 * 신규 원료 코드 신청 코멘트 등록
 * @param {Object} data 요청 데이터
 * @param {number} data.rwmtrNewSeq 신규 원료 코드 신청 번호
 * @param {string} data.comment 코멘트
 * @return {Object} 응답 데이터
 */
async function requestCommentInsert(data) {
  return api.post(API_URL.board.rawMaterial.comment.insert, data);
}

/**
 * 신규 원료 코드 신청 코멘트 수정
 * @param {number} seq 신규 원료 코드 신청 코멘트 번호
 * @param {Object} data 요청 데이터
 * @param {string} data.comment 코멘트
 * @return {Object} 응답 데이터
 */
async function requestCommentUpdate(seq, data) {
  return api.put(
    format(API_URL.board.rawMaterial.comment.update, { seq }),
    data
  );
}

/**
 * 신규 원료 코드 신청 코멘트 삭제
 * @param {number} seq 신규 원료 코드 신청 코멘트 번호
 * @return {Object} 응답 데이터
 */
async function requestCommentDelete(seq) {
  return api.delete(format(API_URL.board.rawMaterial.comment.update, { seq }));
}

/**
 * 신규 원료 코드 신청 승인
 * @param {number} seq 신규 원료 코드 신청 번호
 * @return {Object} 응답 데이터
 */
async function requestConfirm(seq) {
  return api.put(format(API_URL.board.rawMaterial.confirm, { seq }));
}

/**
 * 첨부 파일 다운로드
 * @param {number} seq 첨부 파일 번호
 * @return {Object} 응답 데이터
 */
async function requestDownload(seq) {
  return api.get(format(API_URL.board.rawMaterial.download, { seq }), {
    responseType: 'arraybuffer'
  });
}

/**
 * 신규 원료 코드 신청 반려
 * @param {number} seq 신규 원료 코드 신청 번호
 * @return {Object} 응답 데이터
 */
async function requestReject(seq) {
  return api.put(format(API_URL.board.rawMaterial.reject, { seq }));
}

export {
  requestList,
  requestInsert,
  requestUpdate,
  requestDelete,
  requestInform,
  requestCommentInsert,
  requestCommentUpdate,
  requestCommentDelete,
  requestConfirm,
  requestDownload,
  requestReject
};
